import ExistingMemberCard from "@/components/GetStarted/ExistingMemberCard";
import style from "@/styles/pages/ExistingMember/ExistingMember.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useEffect, useState } from "react";
import {
  clearApplicationState,
  setApplicationId,
  setIsCoApplicant,
  setJointCode,
  setJointPrimaryApplicant,
  setJointRelationship,
} from "@/slices/applicationSlice";
import { clearApplicantState, clearJointState, setApplicantId, setEmailAddress, setFirstName, setLastName, setPhoneNumber } from "@/slices/applicantSlice";
import { clearAddressState } from "@/slices/homeAddressSlice";
import { clearMailingAddressState } from "@/slices/mailingAddressSlice";
import { clearCredentialState, setEmail } from "@/slices/credentialSlice";
import { clearProgressState } from "@/slices/progressSlice";
import { clearLegalTermsState } from "@/slices/legalTermsSlice";
import { RootState } from "@/store/dtm";
import FadeTransition from "@/styles/animation/FadeTransition";
import { clearSessionState, setFinancialInstitutions, setOccupations } from "@/slices/sessionDataSlice";
import { setReferralProducts } from "@/slices/referralProductSlice";
import { setClickInsightEventsNoState } from "@/lib/util";


export default function ExistingMember() {
  const client = createApiClient({ timeout: 60000 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isProductLoading, setIsProductLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );

  // Get Query Parameters (productId)
  let productId = "";
  let inviteCode = "";

  useEffect(() => {

    productId = (searchParams.get("productId") != null) ? decodeURIComponent(searchParams.get("productId") + "") : "";

    if (!application.isCoApplicant) {
      dispatch(clearApplicationState());
    }
    dispatch(clearSessionState());
    dispatch(clearApplicantState());
    dispatch(clearAddressState());
    dispatch(clearMailingAddressState());
    dispatch(clearProgressState());
    dispatch(clearLegalTermsState());
    dispatch(clearJointState());

    inviteCode = (searchParams.get("inviteCode") != null) ? (searchParams.get("inviteCode") + "") : "";
    if (inviteCode != "") {
      client.apiCoapplicants(inviteCode).then(c => {
        if (c != undefined && c.isActiveLink) {
          dispatch(setJointCode(inviteCode));
          dispatch(setIsCoApplicant(true));
          dispatch(setEmail(c.emailAddress ? c.emailAddress : ""));
          dispatch(setEmailAddress(c.emailAddress ? c.emailAddress : ""));
          dispatch(setApplicationId(c.dealId ? c.dealId : 0));
          dispatch(setApplicantId(c.applicantId ? c.applicantId : 0));
          dispatch(setJointRelationship(c.relationship ? c.relationship : ""));
          dispatch(setJointPrimaryApplicant(c.primaryApplicant ? c.primaryApplicant : ""));
          dispatch(setFirstName(c.firstName ? c.firstName : ""));
          dispatch(setLastName(c.lastName ? c.lastName : ""));
          dispatch(setPhoneNumber(c.phoneNumber ? c.phoneNumber : ""));
          setIsLoading(false);
          setClickInsightEventsNoState(
            undefined,
            applicant,
            'applicationStart',
            'co-applicant',
            'new'
          );
        } else {
          navigate("/error/5", { replace: true });
        }
      }).catch(error => {
        console.log("Coapplicant not found");
        if (error && error.response && error.response.code == 5081) {
          navigate("/error/6", { replace: true });
        } else {
          navigate("/error/5", { replace: true });
        }
      });
    } else {
      dispatch(clearCredentialState());
      setIsLoading(false);
      setClickInsightEventsNoState(
        undefined,
        applicant,
        'applicationStart',
        'main',
        'new'
      );
    }

    //Fetch Mortgage Product if ProductID is provided
    if (productId != "") {
      setIsProductLoading(true);
      client.apiProductsDetail(productId)
        .then(products => {
          if (products != undefined && products.length > 0) {
            dispatch(setReferralProducts(products));
          }
        }).catch((error) => {
          setIsProductLoading(false);
          console.error("Error fetching product details:", error);
        }).finally(() => {
          setIsProductLoading(false);
        });
    }

    client
      .apiDataOccupations()
      .then(function (data) {
        dispatch(setOccupations(data != null && data != undefined ? data : []));
      })
      .catch((error) => {
        navigate("/error/3");
      });
    client.apiDataFinancialinstitutions()
      .then(function (data) {
        dispatch(setFinancialInstitutions(data != null && data != undefined ? data : []));
      })
      .catch((error) => {
        navigate("/error/3");
      });
  }, []);

  const handleClick = (type: number) => {
    if (isLoading || isProductLoading) {
      return;
    }

    if (type == 1) {
      navigate("/login");
    } else {
      application.isCoApplicant ? navigate("/joint/accept-terms") : navigate("/single/accept-terms");
    }

  }

  return (
    <FadeTransition>
      <main aria-labelledby="main-content">
        <div className="main-container max-md:mt-10">
          {!isLoading &&
            <>
              <div className={style.emHeading}>
                <div className="grid grid-cols-1">
                  <h1>Are you a Meridian Member or have you</h1>
                  <h1>started an application with us?</h1>
                </div>
              </div>
              <div className="content-container">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="mb-5 sm:w-full">
                    <div onClick={() => handleClick(1)}>
                      <ExistingMemberCard
                        cardHeight={200}
                        cardWidth={255}
                        imageSrc="assets/rob-log-in.svg"
                        cardText="Yes, sign me in"
                        secondaryText="Continue as a member or resume your online application"
                      />
                    </div>
                  </div>
                  <div>
                    <div onClick={() => handleClick(2)}>
                      <ExistingMemberCard
                        cardHeight={200}
                        cardWidth={255}
                        imageSrc="assets/create-account.svg"
                        cardText="No, sign me up"
                        secondaryText="Create your digital profile"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>}
        </div>
      </main>
    </FadeTransition>

  );
}
